import { ArrowLeft, Check, FileText, MoreHorizontal, RotateCcw, X } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { ActivityComposer, ActivityRow } from '../components/ActivityKit';
import { LoseBusinessModal } from '../components/BusinessActionModals';
import { BusinessFiles } from '../components/BusinessFiles';
import { CustomFieldInput } from '../components/CustomFieldInput';
import { InlineEdit } from '../components/InlineEdit';
import { Menu } from '../components/Menu';
import type { BoardLinkState } from '../components/pipelines/KanbanBoard';
import {
  LeadSectionPanel,
  LeadSummaryHeader,
  type SectionKey,
} from '../components/leads/LeadDrawer';
import {
  Button,
  ConfirmDialog,
  EmptyState,
  ErrorNote,
  Loading,
  StatusBadge,
  Tabs,
  inputClass,
} from '../components/ui';
import { errorMessage } from '../lib/api';
import { useAuth } from '../lib/auth-context-value';
import { displayFieldValue } from '../lib/custom-field-display';
import { daysSince, formatDate, formatDateTime, formatMoney, parseMoney } from '../lib/format';
import {
  addBusinessProduct,
  businessFields,
  businessJourney,
  deleteBusiness,
  getBusiness,
  getLead,
  listActivities,
  listAttendants,
  listLeadHistory,
  listStages,
  moveBusinesses,
  pipelinesApi,
  products as productsApi,
  removeBusinessProduct,
  restoreBusinesses,
  setBusinessField,
  updateBusiness,
  winBusinesses,
} from '../lib/resources';
import type { Business, PipelineMemberFlags, Stage } from '../types';

type Tab = 'produtos' | 'campos' | 'anexos' | 'historico' | 'atividades';

const TABS: Array<[Tab, string]> = [
  ['produtos', 'Produtos e Valores'],
  ['campos', 'Campos adicionais'],
  ['anexos', 'Anexos'],
  ['historico', 'Histórico'],
  ['atividades', 'Atividades'],
];

/** "3d 4h", "2h 10min", "agora". */
function formatDuration(ms: number): string {
  const minutes = Math.floor(ms / 60_000);
  if (minutes < 1) return 'menos de 1min';
  const days = Math.floor(minutes / 1440);
  const hours = Math.floor((minutes % 1440) / 60);
  if (days > 0) return `${days}d${hours ? ` ${hours}h` : ''}`;
  const rest = minutes % 60;
  return hours > 0 ? `${hours}h${rest ? ` ${rest}min` : ''}` : `${rest}min`;
}

/**
 * Detalhe do negócio (doc 03): a coluna do lead à esquerda (a mesma da ficha)
 * e, à direita, os cartões Número/Valor/Data, a Pipeline completa ou a
 * Jornada, e as abas do negócio. Clicar numa seção do lead troca o painel
 * da direita pela seção; "Voltar ao negócio" desfaz.
 */
export function BusinessDetailPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<Tab>('produtos');
  const [leadSection, setLeadSection] = useState<SectionKey | null>(null);
  const [losing, setLosing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const businessQuery = useQuery({
    queryKey: ['business', id],
    queryFn: () => getBusiness(id),
    enabled: Boolean(id),
  });
  const business = businessQuery.data;
  const pipelineId = business?.stage?.pipelineId;

  const leadQuery = useQuery({
    queryKey: ['lead', business?.leadId],
    queryFn: () => getLead(business!.leadId),
    enabled: Boolean(business?.leadId),
  });
  const pipelineQuery = useQuery({
    queryKey: ['pipeline', pipelineId],
    queryFn: () => pipelinesApi.get(pipelineId!),
    enabled: Boolean(pipelineId),
  });
  const flags: PipelineMemberFlags = pipelineQuery.data?.myFlags ?? {
    disableAttendantChange: false,
    showOnlyMine: false,
    disableCreate: false,
    disableDelete: false,
  };

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['business', id] });
    queryClient.invalidateQueries({ queryKey: ['business-journey', id] });
    queryClient.invalidateQueries({ queryKey: ['kanban'] });
    queryClient.invalidateQueries({ queryKey: ['lead-history', business?.leadId] });
    queryClient.invalidateQueries({ queryKey: ['activities'] });
  };
  const fail = (fallback: string) => (mutationError: unknown) =>
    setError(errorMessage(mutationError, fallback));

  const win = useMutation({
    mutationFn: () => winBusinesses([id]),
    onSuccess: refresh,
    onError: fail('Falha ao ganhar'),
  });
  const restore = useMutation({
    mutationFn: () => restoreBusinesses([id]),
    onSuccess: refresh,
    onError: fail('Falha ao restaurar'),
  });
  const remove = useMutation({
    mutationFn: () => deleteBusiness(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kanban'] });
      navigate(`/pipelines${pipelineId ? `?p=${pipelineId}` : ''}`);
    },
  });

  if (businessQuery.isLoading) return <Loading label="Carregando negócio…" />;
  if (businessQuery.isError || !business) {
    return (
      <div className="p-6">
        <ErrorNote>{errorMessage(businessQuery.error, 'Negócio não encontrado')}</ErrorNote>
        <Button className="mt-4" onClick={() => navigate('/pipelines')}>
          Voltar às pipelines
        </Button>
      </div>
    );
  }

  // aberto pelo card do kanban: volta para o mesmo quadro, com os mesmos filtros
  const fromBoard = (location.state as Partial<BoardLinkState> | null)?.board;
  const backToBoard = fromBoard?.startsWith('/pipelines')
    ? fromBoard
    : `/pipelines${pipelineId ? `?p=${pipelineId}` : ''}`;
  const lead = leadQuery.data;

  return (
    <div className="flex h-full">
      {lead ? (
        <LeadSummaryHeader
          lead={lead}
          section={leadSection ?? 'negocios'}
          onSection={setLeadSection}
          onClose={() => navigate(backToBoard)}
          asPage
        />
      ) : (
        <aside className="w-72 shrink-0 border-r border-slate-200 bg-slate-50">
          <Loading label="Carregando lead…" />
        </aside>
      )}

      <section className="min-w-0 flex-1 overflow-y-auto bg-slate-50">
        <header className="border-b border-slate-200 bg-white px-6 py-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <Link to={backToBoard} className="text-xs text-slate-400 hover:underline">
                <span className="inline-flex items-center gap-1">
                  <ArrowLeft size={12} /> {pipelineQuery.data?.name ?? 'Pipeline'}
                </span>
              </Link>
              <div className="flex items-center gap-2">
                <h1 className="text-lg font-semibold text-slate-900">
                  Negócio #{business.code}
                </h1>
                <StatusBadge status={business.status} />
              </div>
            </div>
            <div className="flex gap-2">
              {business.status === 'in_process' ? (
                <>
                  <Button
                    onClick={() => win.mutate()}
                    disabled={win.isPending}
                    className="border-emerald-200 bg-emerald-50 text-emerald-700"
                  >
                    <Check size={14} /> Ganhar
                  </Button>
                  <Button variant="danger" onClick={() => setLosing(true)}>
                    <X size={14} /> Perder
                  </Button>
                </>
              ) : (
                <Button onClick={() => restore.mutate()}>
                  <RotateCcw size={14} /> Restaurar status
                </Button>
              )}
              {/* Módulo Vendas (V4): proposta já ligada a este negócio e ao lead. */}
              {can('sales_admin', 'sales_member') && (
                <Button onClick={() => navigate(`/vendas?negocio=${business.id}`)}>
                  <FileText size={14} /> Proposta de venda
                </Button>
              )}
              <Menu
                trigger={(toggle) => (
                  <Button onClick={toggle} aria-label="Mais ações do negócio">
                    <MoreHorizontal size={15} />
                  </Button>
                )}
                groups={[
                  {
                    items: [
                      {
                        label: 'Excluir negócio',
                        danger: true,
                        disabled: flags.disableDelete,
                        hint: flags.disableDelete ? 'Sem permissão nesta pipeline' : undefined,
                        onSelect: () => setDeleting(true),
                      },
                    ],
                  },
                ]}
              />
            </div>
          </div>
          {error && (
            <div className="mt-3">
              <ErrorNote>{error}</ErrorNote>
            </div>
          )}
        </header>

        {leadSection && lead ? (
          <div className="p-6">
            <button
              type="button"
              onClick={() => setLeadSection(null)}
              className="mb-4 inline-flex items-center gap-1 text-[13px] font-medium text-slate-600 hover:underline"
            >
              <ArrowLeft size={14} /> Voltar ao negócio
            </button>
            <div className="rounded-xl border border-slate-200 bg-white p-5">
              <LeadSectionPanel lead={lead} section={leadSection} />
            </div>
          </div>
        ) : (
          <div className="space-y-5 p-6">
            <BusinessCards
              business={business}
              flags={flags}
              onChange={refresh}
              onError={setError}
            />
            {pipelineId && (
              <PipelinePanel
                business={business}
                pipelineId={pipelineId}
                onMoved={refresh}
                onError={setError}
              />
            )}
            <div className="rounded-xl border border-slate-200 bg-white">
              <div className="px-4 pt-2">
                <Tabs tabs={TABS} value={tab} onChange={setTab} />
              </div>
              <div className="p-4">
                {tab === 'produtos' && <ProductsTab business={business} onChange={refresh} />}
                {tab === 'campos' && <FieldsTab business={business} />}
                {tab === 'anexos' && (
                  <BusinessFiles leadId={business.leadId} businessId={business.id} />
                )}
                {tab === 'historico' && <HistoryTab business={business} />}
                {tab === 'atividades' && <ActivitiesTab business={business} />}
              </div>
            </div>
          </div>
        )}
      </section>

      {losing && (
        <LoseBusinessModal businessId={id} onClose={() => setLosing(false)} onDone={refresh} />
      )}
      {deleting && (
        <ConfirmDialog
          title="Excluir negócio"
          message={
            <>
              O negócio <strong>#{business.code}</strong> vai para a Lixeira com as atividades
              dele e pode ser restaurado em até 30 dias.
            </>
          }
          confirmLabel="Excluir"
          danger
          busy={remove.isPending}
          error={remove.error ? errorMessage(remove.error, 'Falha ao excluir') : null}
          onConfirm={() => remove.mutate()}
          onClose={() => setDeleting(false)}
        />
      )}
    </div>
  );
}

/** Cartões Número · Valor · Data de criação · Atendente. */
function BusinessCards({
  business,
  flags,
  onChange,
  onError,
}: {
  business: Business;
  flags: PipelineMemberFlags;
  onChange: () => void;
  onError: (message: string | null) => void;
}) {
  const attendants = useQuery({ queryKey: ['attendants'], queryFn: listAttendants });
  const attendant = useMutation({
    mutationFn: (attendantId: string) => updateBusiness(business.id, { attendantId }),
    onSuccess: () => {
      onError(null);
      onChange();
    },
    onError: (mutationError) =>
      onError(errorMessage(mutationError, 'Não foi possível trocar o atendente')),
  });
  const creator = attendants.data?.data.find((item) => item.userId === business.createdById);

  return (
    <div className="grid gap-3 md:grid-cols-4">
      <Card label="Número" value={`#${business.code}`} />
      <Card label="Valor" value={formatMoney(business.total)} />
      <Card
        label="Data de criação"
        value={formatDate(business.createdAt)}
        hint={`${daysSince(business.createdAt)}d${creator ? ` · por ${creator.name}` : ''}`}
      />
      <div className="rounded-xl border border-slate-200 bg-white px-4 py-3">
        <p className="text-[11px] uppercase tracking-wide text-slate-400">Atendente</p>
        <select
          className={`${inputClass} mt-1 py-1`}
          value={business.attendantId ?? ''}
          disabled={flags.disableAttendantChange || attendant.isPending}
          title={
            flags.disableAttendantChange ? 'Sem permissão para trocar o atendente' : undefined
          }
          onChange={(event) => attendant.mutate(event.target.value)}
        >
          <option value="" disabled>
            Sem atendente
          </option>
          {(attendants.data?.data ?? []).map((item) => (
            <option key={item.id} value={item.userId}>
              {item.name}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}

function Card({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white px-4 py-3">
      <p className="text-[11px] uppercase tracking-wide text-slate-400">{label}</p>
      <p className="mt-1 text-sm font-semibold text-slate-800">{value}</p>
      {hint && <p className="text-xs text-slate-400">{hint}</p>}
    </div>
  );
}

/**
 * "Pipeline completa": as etapas na ordem, com a atual marcada — clicar numa
 * etapa move o negócio (as condicionais podem barrar). "Jornada": por onde
 * o negócio passou e quanto tempo ficou em cada etapa.
 */
function PipelinePanel({
  business,
  pipelineId,
  onMoved,
  onError,
}: {
  business: Business;
  pipelineId: string;
  onMoved: () => void;
  onError: (message: string | null) => void;
}) {
  const [mode, setMode] = useState<'pipeline' | 'jornada'>('pipeline');
  const [confirmStage, setConfirmStage] = useState<Stage | null>(null);
  const stages = useQuery({
    queryKey: ['stages', pipelineId],
    queryFn: () => listStages(pipelineId),
  });
  const journey = useQuery({
    queryKey: ['business-journey', business.id],
    queryFn: () => businessJourney(business.id),
    enabled: mode === 'jornada',
  });
  const move = useMutation({
    mutationFn: (stage: Stage) => moveBusinesses([business.id], stage.id),
    onSuccess: () => {
      setConfirmStage(null);
      onError(null);
      onMoved();
    },
    onError: (mutationError) => {
      setConfirmStage(null);
      onError(errorMessage(mutationError, 'Não foi possível mover o negócio'));
    },
  });

  const list = stages.data ?? [];
  const currentIndex = list.findIndex((stage) => stage.id === business.stageId);

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <div className="mb-3 flex items-center gap-1 text-sm">
        {(
          [
            ['pipeline', 'Pipeline completa'],
            ['jornada', 'Jornada'],
          ] as const
        ).map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => setMode(key)}
            className={`rounded-md px-3 py-1 ${mode === key ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-100'}`}
          >
            {label}
          </button>
        ))}
      </div>

      {mode === 'pipeline' ? (
        <div className="flex gap-1 overflow-x-auto pb-1">
          {list.map((stage, index) => {
            const isCurrent = stage.id === business.stageId;
            const passed = currentIndex >= 0 && index < currentIndex;
            return (
              <button
                key={stage.id}
                type="button"
                disabled={isCurrent || move.isPending}
                onClick={() => setConfirmStage(stage)}
                title={isCurrent ? 'Etapa atual' : `Mover para ${stage.name}`}
                className={`min-w-28 flex-1 rounded-md px-3 py-2 text-xs font-medium transition ${
                  isCurrent
                    ? 'text-white'
                    : passed
                      ? 'bg-slate-200 text-slate-700 hover:bg-slate-300'
                      : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                }`}
                style={
                  isCurrent
                    ? { backgroundColor: stage.color ?? 'var(--crm-primary)' }
                    : undefined
                }
              >
                {stage.name}
              </button>
            );
          })}
        </div>
      ) : journey.isLoading ? (
        <Loading />
      ) : (
        <ol className="space-y-2">
          {(journey.data ?? []).map((entry, index, all) => (
            <li key={entry.id} className="flex items-start gap-3 text-sm">
              <span
                className={`mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full ${
                  index === all.length - 1 ? 'bg-emerald-500' : 'bg-slate-300'
                }`}
              />
              <div className="flex-1">
                <p className="text-slate-800">
                  {entry.fromStage ? (
                    <>
                      {entry.fromStage.name} → <strong>{entry.toStage.name}</strong>
                    </>
                  ) : (
                    <>
                      Criado em <strong>{entry.toStage.name}</strong>
                    </>
                  )}
                </p>
                <p className="text-xs text-slate-400">
                  {formatDateTime(entry.movedAt)}
                  {entry.movedBy.name ? ` · ${entry.movedBy.name}` : ''} · ficou{' '}
                  {formatDuration(entry.durationMs)}
                  {index === all.length - 1 && business.status === 'in_process'
                    ? ' (até agora)'
                    : ''}
                </p>
              </div>
            </li>
          ))}
        </ol>
      )}

      {confirmStage && (
        <ConfirmDialog
          title="Mover negócio"
          message={
            <>
              Mover o negócio #{business.code} para <strong>{confirmStage.name}</strong>?
              {confirmStage.autoActivitiesCount > 0 && (
                <span className="mt-2 block text-sky-700">
                  A etapa vai criar {confirmStage.autoActivitiesCount} atividade(s)
                  automática(s).
                </span>
              )}
            </>
          }
          confirmLabel="Mover"
          busy={move.isPending}
          onConfirm={() => move.mutate(confirmStage)}
          onClose={() => setConfirmStage(null)}
        />
      )}
    </div>
  );
}

/** Produtos e Valores: com produtos o total é calculado; sem, vale o digitado (Q19). */
function ProductsTab({ business, onChange }: { business: Business; onChange: () => void }) {
  const [productId, setProductId] = useState('');
  const [quantity, setQuantity] = useState('1');
  const [adjustments, setAdjustments] = useState({
    discount: String(business.discount / 100),
    addition: String(business.addition / 100),
    shipping: String(business.shipping / 100),
  });
  const productsQuery = useQuery({
    queryKey: ['products'],
    queryFn: () => productsApi.list({ take: 200 }),
  });

  const addProduct = useMutation({
    mutationFn: () =>
      addBusinessProduct(business.id, {
        productId,
        quantity: Number.parseInt(quantity, 10) || 1,
      }),
    onSuccess: () => {
      setProductId('');
      setQuantity('1');
      onChange();
    },
  });
  const removeProduct = useMutation({
    mutationFn: (itemId: string) => removeBusinessProduct(business.id, itemId),
    onSuccess: onChange,
  });
  const saveAdjustments = useMutation({
    mutationFn: () =>
      updateBusiness(business.id, {
        discount: parseMoney(adjustments.discount),
        addition: parseMoney(adjustments.addition),
        shipping: parseMoney(adjustments.shipping),
      }),
    onSuccess: onChange,
  });

  const items = business.products ?? [];
  const productsTotal = items.reduce((sum, item) => sum + item.total, 0);

  return (
    <div className="space-y-4">
      <div className="divide-y divide-slate-100 rounded-lg border border-slate-200">
        {items.map((item) => (
          <div key={item.id} className="flex items-center justify-between px-4 py-3 text-sm">
            <div>
              <p className="font-medium text-slate-800">{item.product.name}</p>
              <p className="text-xs text-slate-400">
                {item.quantity} × {formatMoney(item.price)}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <span className="font-medium text-slate-700">{formatMoney(item.total)}</span>
              <button
                type="button"
                onClick={() => removeProduct.mutate(item.id)}
                className="text-xs text-slate-400 hover:text-red-600"
              >
                remover
              </button>
            </div>
          </div>
        ))}
        {items.length === 0 && (
          <div className="px-4 py-4">
            <InlineEdit
              label="Valor do negócio (sem produtos)"
              value={String(business.total / 100)}
              display={formatMoney(business.total)}
              onSave={async (value) => {
                await updateBusiness(business.id, { total: parseMoney(value ?? '0') });
                onChange();
              }}
            />
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-end gap-2">
        <label className="text-sm">
          <span className="text-xs text-slate-500">Produto</span>
          <select
            className={`${inputClass} w-56`}
            value={productId}
            onChange={(event) => setProductId(event.target.value)}
          >
            <option value="">Selecione…</option>
            {(productsQuery.data?.data ?? []).map((product) => (
              <option key={product.id} value={product.id}>
                {product.name} — {formatMoney(product.price)}
              </option>
            ))}
          </select>
        </label>
        <label className="text-sm">
          <span className="text-xs text-slate-500">Qtd</span>
          <input
            type="number"
            min={1}
            className={`${inputClass} w-20`}
            value={quantity}
            onChange={(event) => setQuantity(event.target.value)}
          />
        </label>
        <Button
          variant="primary"
          disabled={!productId || addProduct.isPending}
          onClick={() => addProduct.mutate()}
        >
          Adicionar produto
        </Button>
      </div>

      {items.length > 0 && (
        <div className="space-y-2 border-t border-slate-200 pt-3 text-sm">
          <Row label="Produtos" value={formatMoney(productsTotal)} />
          <div className="flex flex-wrap items-end gap-2">
            {(
              [
                ['discount', 'Desconto −'],
                ['addition', 'Acréscimo +'],
                ['shipping', 'Frete +'],
              ] as const
            ).map(([key, label]) => (
              <label key={key} className="text-sm">
                <span className="text-xs text-slate-500">{label}</span>
                <input
                  className={`${inputClass} w-28`}
                  value={adjustments[key]}
                  onChange={(event) =>
                    setAdjustments({ ...adjustments, [key]: event.target.value })
                  }
                />
              </label>
            ))}
            <Button
              onClick={() => saveAdjustments.mutate()}
              disabled={saveAdjustments.isPending}
            >
              Aplicar
            </Button>
          </div>
          <div className="border-t border-slate-100 pt-2">
            <Row label="Total" value={formatMoney(business.total)} strong />
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div className="flex justify-between">
      <span className={strong ? 'font-semibold text-slate-800' : 'text-slate-500'}>
        {label}
      </span>
      <span className={strong ? 'font-semibold text-slate-900' : 'text-slate-700'}>
        {value}
      </span>
    </div>
  );
}

function FieldsTab({ business }: { business: Business }) {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ['business-fields', business.id],
    queryFn: () => businessFields(business.id),
  });
  if (query.isLoading) return <Loading />;
  const fields = query.data ?? [];
  if (fields.length === 0) {
    return (
      <EmptyState
        title="Nenhum campo adicional de negócio"
        hint="Cadastre em Configurações › Campos adicionais › Negócio."
      />
    );
  }
  return (
    <div className="grid gap-x-6 md:grid-cols-2">
      {fields.map((field) => (
        <InlineEdit
          key={field.id}
          label={field.name}
          value={field.value}
          display={displayFieldValue(field, field.value)}
          onSave={async (value) => {
            const next = await setBusinessField(business.id, field.id, value);
            queryClient.setQueryData(['business-fields', business.id], next);
          }}
          renderInput={(draft, setDraft) => (
            <CustomFieldInput field={field} value={draft} onChange={setDraft} />
          )}
        />
      ))}
    </div>
  );
}

function HistoryTab({ business }: { business: Business }) {
  const query = useQuery({
    queryKey: ['lead-history', business.leadId, business.id],
    queryFn: () => listLeadHistory(business.leadId, business.id),
  });
  if (query.isLoading) return <Loading />;
  const entries = query.data ?? [];
  return (
    <ol className="space-y-3">
      {entries.map((entry) => (
        <li key={entry.id} className="rounded-lg border border-slate-200 px-4 py-3">
          <p className="text-sm text-slate-800">{entry.history ?? entry.note}</p>
          <p className="mt-1 text-[11px] text-slate-400">
            {entry.authorType === 'AUTOMATION' ? 'Automação ' : ''}
            {entry.authorName ?? 'Sistema'} · {formatDateTime(entry.createdAt)}
          </p>
        </li>
      ))}
      {entries.length === 0 && (
        <li className="text-xs text-slate-400">Nenhum evento registrado.</li>
      )}
    </ol>
  );
}

function ActivitiesTab({ business }: { business: Business }) {
  const query = useQuery({
    queryKey: ['activities', 'business', business.id],
    queryFn: () => listActivities({ businessId: business.id, take: 100 }),
  });
  const activities = query.data?.data ?? [];
  const open = activities.filter((activity) => !activity.isCompleted);

  return (
    <div className="space-y-4">
      <ActivityComposer businessId={business.id} compact />
      {query.isLoading ? (
        <Loading />
      ) : (
        <section className="overflow-hidden rounded-lg border border-slate-200">
          <header className="border-b border-slate-200 px-4 py-2 text-sm font-semibold text-slate-800">
            {open.length} aberta(s) · {activities.length} no total
          </header>
          <div className="divide-y divide-slate-100">
            {activities.map((activity) => (
              <ActivityRow key={activity.id} activity={activity} showBusinessLink={false} />
            ))}
            {activities.length === 0 && (
              <p className="px-4 py-8 text-center text-xs text-slate-400">
                Nenhuma atividade neste negócio.
              </p>
            )}
          </div>
        </section>
      )}
    </div>
  );
}
